import { Box, Button, Code, Heading, Text, VStack } from '@chakra-ui/react'
import { Component } from 'react'

/**
 * Error Boundary Component
 * Catches React errors in component tree
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={ 4 }>
          <VStack spacing={ 4 } maxW="600px" textAlign="center">
            <Heading size="xl" color="red.500">
              Oops! Something went wrong
            </Heading>

            <Text color="text.secondary">
              We're sorry for the inconvenience. An error occurred while rendering this page.
            </Text>

            {this.props.showDetails && this.state.error && (
              <Box
                w="full"
                p={ 4 }
                bg="bg.secondary"
                borderRadius="md"
                textAlign="left"
                overflow="auto"
                maxH="300px"
              >
                <Text fontWeight="bold" mb={ 2 }>
                  Error Details:
                </Text>
                <Code display="block" whiteSpace="pre-wrap" fontSize="sm">
                  { this.state.error.toString() }
                  { this.state.errorInfo?.componentStack }
                </Code>
              </Box>
            )}

            <Button variant="solid" onClick={ this.handleReset }>
              Return to Home
            </Button>
          </VStack>
        </Box>
      )
    }

    return this.props.children
  }
}

/**
 * Hook-based error boundary wrapper
 * Use this for functional component syntax
 */
export function withErrorBoundary(Component, showDetails = false) {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary showDetails={ showDetails }>
        <Component { ...props } />
      </ErrorBoundary>
    )
  }
}

export default ErrorBoundary
